import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  LinearProgress,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import * as UpChunk from '@mux/upchunk';
import { api, fetchData } from '../../helpers/apiUtils';
import {
  Abschnitt,
  Feld,
  GefahrBestaetigung,
  NurAdmin,
  datumDe,
  useFeldpruefung,
  useIstAdmin,
} from '../../components/marketingForm';
import { getLivePreis, type LivePreisGrund } from '../webinar-lp/funnel/api';
import {
  PRODUKT_SLOT_LABEL,
  PRODUKT_VARIANTEN,
  PRODUKT_VARIANTE_LABEL,
  ladeProduktMedien,
} from '../webinar-lp/funnel/produktMedien/api';
import { DoiKarteEditor, DoiKarteStatus, useDoiKarte } from './DoiCardFactsPanel';
import WerbebildPanel from './WerbebildPanel';

/**
 * Eigener Webinar-Raum (live.finanztrends.de/webinar/<id>) — Video, Angebot und Probe je Webinar.
 *
 * Aufbau seit Plan 2026-10-22 (A3, „Marketing versteht es auf den ersten Blick“), Reihenfolge =
 * Arbeitsreihenfolge: Kopf mit Status in Worten · Video (Pflicht) · Kaufangebot (Pflicht für den Verkauf) ·
 * Warteraum-Bild (optional, zu) · Testen · Läuft automatisch (Portal-Werbung, DOI-Karte, Untertitel) ·
 * Notfall (Raum sperren) · Technik (nur Admin). Gespeichert wird JE ABSCHNITT und nur, was sich gegenüber dem
 * geladenen Stand geändert hat — der PUT nimmt Teilmengen (roomSettings.normalizeRoomSettings liest nur
 * mitgeschickte Schlüssel). Admin-Felder gehen beim Speichern des Angebots deshalb gar nicht mit und können
 * weder verloren gehen noch still überschrieben werden.
 *
 * Der Raum rechnet seine Phase selbst aus Termin + Videolaenge; hier wird nur eingestellt, WAS er
 * zeigt. Das Video geht direkt vom Browser zu Mux (Direct Upload, wie die Werbevideo-Bibliothek),
 * deutsche Untertitel erzeugt Mux automatisch. Der Status wird beim Laden gegen Mux abgeglichen,
 * deshalb pollt der Reiter, solange etwas verarbeitet wird.
 */

interface RoomConfig {
  room_enabled: number;
  recorded_on: string | null;
  video_source: 'upload' | 'url' | null;
  video_input_url: string | null;
  mux_upload_id: string | null;
  mux_asset_id: string | null;
  mux_playback_id: string | null;
  video_status: 'none' | 'uploading' | 'preparing' | 'ready' | 'errored';
  /** „Video kommt später“ (03.11.2026): zugesagt bis (Berliner Wandzeit), null = keine Zusage. */
  video_expected_by?: string | null;
  video_error: string | null;
  video_duration_sec: number | null;
  captions_status: 'none' | 'preparing' | 'ready' | 'errored';
  offer_enabled: number;
  offer_from_minute: number | null;
  offer_title: string | null;
  offer_subline?: string | null;
  offer_text: string | null;
  offer_bonus_text: string | null;
  offer_button_label: string | null;
  offer_image_url?: string | null;
  offer_image_pos?: 'top' | 'right' | null;
  offer_bullets?: string | null;
  offer_highlight?: string | null;
  offer_order_url: string | null;
  offer_mode: 'redirect' | 'embed';
  plx_campaign_id: string | null;
  poster_time_sec?: number | null;
  poster_image_url?: string | null;
  /** Frueherer Raum-Rollout — ohne Wirkung seit der SS-Abschaltung 02.10. (nur noch Historie). */
  rollout_percent?: number;
  /** Berliner Wandzeit `YYYY-MM-DDTHH:mm`; ohne Wirkung seit 02.10. */
  rollout_from?: string | null;
  /** Kaufweg-A/B: Bestell-URL Arm H (gehostet); leer = kein A/B / Not-Aus (alle P). */
  offer_order_url_h?: string | null;
  /** Kaufweg-A/B: Anteil H in % fuer neue Zuschauer (Arm wird beim ersten Zugang eingefroren). */
  purchase_ab_percent?: number;
  /** Webinar-Promo auf den Portalen (webinar-promo.js); fehlt = 1. */
  promo_enabled?: number;
  /** Bonus-Ende fuer die Wiederholung, Berliner Wandzeit `YYYY-MM-DDTHH:mm`; null = kein Bonus dort. */
  offer_bonus_until?: string | null;
  /** Not-Aus seit (Berliner Wandzeit `YYYY-MM-DD HH:mm:ss`) / von wem; null = an oder vor Migration 20261008. */
  room_disabled_at?: string | null;
  room_disabled_by?: string | null;
  /** Webinar-Karte auf den DOI-Danke-Seiten; leer = automatisch. Fehlt vor Migration 20261020. */
  doi_headline?: string | null;
  doi_subline?: string | null;
  doi_image_url?: string | null;
  updated_by: string | null;
}

interface RoomView {
  webinar: {
    webinar_id: string;
    /** Oeffentlicher Titel (Altbestand ohne: Arbeitstitel). */
    title: string | null;
    /** Oeffentlicher Titel, wie gespeichert (null = fehlt). */
    public_title?: string | null;
    /** Interner Arbeitstitel — nie oeffentlich. */
    internal_title?: string | null;
    /** Gesetzt, wenn der Termin einen oeffentlichen Titel braucht und keiner da ist (Raum laesst sich nicht einschalten). */
    public_title_problem?: string | null;
    host_name: string | null;
    start_date: string;
    is_evergreen: boolean;
    is_recurring: boolean;
    order_url: string | null;
    missed_webinar_url: string | null;
    signup_url: string | null;
  };
  config: RoomConfig | null;
  room_url: string;
  preview_hls_url: string | null;
  video_locked: boolean;
  order_link_preview: string | null;
  /** Kaufweg-A/B: Vorschau Arm H; null = keine H-URL. */
  order_link_preview_h?: string | null;
  replay_redirect_preview: string | null;
  rollout_counts?: { room: number; ss: number; testers: number };
  /** Ende des Spaeteinlasses nach Sendestart (lateEntry.ts); null = keiner. */
  late_entry_until_ms?: number | null;
  /** Aufzeichnungsseite zeigt den Kaufknopf sofort, obwohl der Raum ein Angebot hat (lpRecordingCtaHint.ts). */
  recording_cta_hint?: string | null;
}

interface FormState {
  recorded_on: string;
  offer_enabled: boolean;
  offer_from_minute: string;
  offer_title: string;
  offer_subline: string;
  offer_text: string;
  offer_bonus_text: string;
  /** datetime-local `YYYY-MM-DDTHH:mm` oder leer. */
  offer_bonus_until: string;
  offer_button_label: string;
  offer_image_url: string;
  offer_image_pos: 'top' | 'right';
  offer_bullets: string;
  offer_highlight: string;
  offer_order_url: string;
  offer_order_url_h: string;
  purchase_ab_percent: string;
  offer_mode: 'redirect' | 'embed';
  plx_campaign_id: string;
  poster_time_sec: string;
  poster_image_url: string;
  doi_headline: string;
  doi_subline: string;
  doi_image_url: string;
}

/** Wie der Server (roomSettings.ts TEXT_LIMITS): laengere Texte schneidet er ab. */
const DOI_HEADLINE_MAX = 80;
const DOI_SUBLINE_MAX = 70;

/** Ein Satz je Ablehnungsgrund von „Preis aus der Bestellseite übernehmen" — was Marketing tun kann. */
const LIVE_PREIS_HINWEIS: Record<LivePreisGrund, string> = {
  keine_seite: 'Dieses Webinar hat noch keine eigene Bestellseite — es gibt keinen Preis zum Übernehmen.',
  nicht_publiziert:
    'Die Bestellseite dieses Webinars ist noch nicht veröffentlicht — es gibt noch keinen Preis zum Übernehmen.',
  ohne_fingerabdruck:
    'Bei der Bestellseite ist nicht sicher, ob der gespeicherte Stand live ist — bitte die Strecke einmal neu veröffentlichen, dann übernehmen.',
  unveroeffentlicht:
    'Die Bestellseite hat gespeicherte, noch nicht veröffentlichte Änderungen — erst veröffentlichen, dann den Preis übernehmen.',
  kein_preis: 'Die veröffentlichte Bestellseite hat keine Preiszeile — bitte den Preis von Hand eintragen.',
  platzhalter:
    'Die Preiszeile der Bestellseite enthält ein Datum, das sich mit dem Stichtag ändert — bitte den Preis von Hand eintragen.',
};

/** DB-Wert (mysql2-JSON `2026-11-04T12:00:00.000Z` = rohe Berliner Wandzeit, oder `2026-11-04 12:00:00`) → `YYYY-MM-DDTHH:mm`. */
function zusageEingabe(wert: string | null | undefined): string {
  return wert ? String(wert).replace(' ', 'T').slice(0, 16) : '';
}

/** „Video kommt später“: Zusage, bis wann das Raum-Video kommt — dann darf das Webinar schon beworben werden. */
const VideoZusageKasten: React.FC<{
  webinarId: string;
  wert: string | null;
  onView: (v: RoomView) => void;
}> = ({ webinarId, wert, onView }) => {
  const gespeichert = zusageEingabe(wert);
  const [eingabe, setEingabe] = useState(gespeichert);
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  useEffect(() => setEingabe(gespeichert), [gespeichert]);
  const speichern = async (neu: string | null) => {
    setLaeuft(true);
    setFehler(null);
    const r = await fetchData<RoomView>(`/api/webinar-room-admin/${encodeURIComponent(webinarId)}/video-zusage`, {
      method: 'PUT',
      body: JSON.stringify({ expected_by: neu }),
    });
    setLaeuft(false);
    if (r.success && r.data) onView(r.data);
    else setFehler(r.error || 'Speichern fehlgeschlagen');
  };
  const anzeige = gespeichert
    ? `${gespeichert.slice(8, 10)}.${gespeichert.slice(5, 7)}. um ${gespeichert.slice(11, 16)} Uhr`
    : '';
  return (
    <Box mt={2} p={1.5} sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1 }} data-testid="video-zusage">
      <Typography variant="subtitle2">Video kommt später</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Das Video ist noch nicht fertig? Trag ein, bis wann es kommt — dann darf das Webinar schon beworben werden.
        Angemeldete sehen bis zum Start „Ihr Platz ist reserviert“. Fehlt das Video zur Zusage, 24 und 6 Stunden vor dem
        Start, gehen Erinnerungen per Mail an Marketing. Spätestens 2 Stunden vor dem Start.
      </Typography>
      {gespeichert && (
        <Alert severity="info" sx={{ mb: 1 }}>
          Zugesagt bis {anzeige} — das Webinar darf beworben werden. Bitte das Video bis dahin hochladen.
        </Alert>
      )}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
        <TextField
          type="datetime-local"
          size="small"
          label="Video kommt bis"
          value={eingabe}
          onChange={(e) => setEingabe(e.target.value)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ 'aria-label': 'Video kommt bis' }}
        />
        <Button variant="outlined" onClick={() => void speichern(eingabe || null)} disabled={laeuft || !eingabe}>
          {laeuft ? 'Speichert …' : 'Zusage speichern'}
        </Button>
        {gespeichert && (
          <Button color="inherit" onClick={() => void speichern(null)} disabled={laeuft}>
            Zusage entfernen
          </Button>
        )}
      </Stack>
      {fehler && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {fehler}
        </Alert>
      )}
    </Box>
  );
};

const EMPTY_FORM: FormState = {
  recorded_on: '',
  offer_enabled: false,
  offer_from_minute: '',
  offer_title: '',
  offer_subline: '',
  offer_text: '',
  offer_bonus_text: '',
  offer_bonus_until: '',
  offer_button_label: '',
  offer_image_url: '',
  offer_image_pos: 'top',
  offer_bullets: '',
  offer_highlight: '',
  offer_order_url: '',
  offer_order_url_h: '',
  purchase_ab_percent: '0',
  offer_mode: 'redirect',
  plx_campaign_id: '',
  poster_time_sec: '',
  poster_image_url: '',
  doi_headline: '',
  doi_subline: '',
  doi_image_url: '',
};

function formFromConfig(c: RoomConfig | null): FormState {
  if (!c) return EMPTY_FORM;
  return {
    recorded_on: c.recorded_on || '',
    offer_enabled: Number(c.offer_enabled) === 1,
    offer_from_minute: c.offer_from_minute == null ? '' : String(c.offer_from_minute),
    offer_title: c.offer_title || '',
    offer_subline: c.offer_subline || '',
    offer_text: c.offer_text || '',
    offer_bonus_text: c.offer_bonus_text || '',
    offer_bonus_until: c.offer_bonus_until || '',
    offer_button_label: c.offer_button_label || '',
    offer_image_url: c.offer_image_url || '',
    offer_image_pos: c.offer_image_pos === 'right' ? 'right' : 'top',
    offer_bullets: c.offer_bullets || '',
    offer_highlight: c.offer_highlight || '',
    offer_order_url: c.offer_order_url || '',
    offer_order_url_h: c.offer_order_url_h || '',
    purchase_ab_percent: String(Number(c.purchase_ab_percent) || 0),
    offer_mode: c.offer_mode === 'embed' ? 'embed' : 'redirect',
    plx_campaign_id: c.plx_campaign_id || '',
    poster_time_sec: c.poster_time_sec == null ? '' : String(c.poster_time_sec),
    poster_image_url: c.poster_image_url || '',
    doi_headline: c.doi_headline || '',
    doi_subline: c.doi_subline || '',
    doi_image_url: c.doi_image_url || '',
  };
}

/** Wie der Server (roomSettings.ts POSTER_DEFAULT_TIME_SEC): ohne Angabe Standbild bei Sekunde 8. */
const POSTER_DEFAULT_TIME_SEC = 8;
/**
 * Vorschlaege fuer die Standbild-Leiste: Sekunde 8 (Vorgabe) plus 11 Stellen gleichmaessig ueber das Video.
 * Im Warteraum liegen Countdown und Titel LINKS — gute Standbilder zeigen den Referenten rechts.
 */
function posterCandidates(durationSec: number): number[] {
  if (!(durationSec > 60)) return [POSTER_DEFAULT_TIME_SEC];
  const out = [POSTER_DEFAULT_TIME_SEC];
  for (let i = 1; i <= 11; i += 1) out.push(Math.round((durationSec * i) / 12));
  return out;
}

/**
 * Sekunde des Standbilds aus dem Formularwert — dieselbe Regel wie der Server (roomSettings.ts
 * posterUrl): leer → Vorgabe, sonst ganze Sekunde >= 0. Sekunde 0 ist ein gueltiger Wert; ein
 * `Number(v) || 8` machte daraus 8, und die Vorschau zeigte ein anderes Bild als der Raum.
 */
function posterSecOf(v: string): number {
  const t = v.trim();
  if (!t) return POSTER_DEFAULT_TIME_SEC;
  const n = Number(t);
  return Number.isFinite(n) && n >= 0 ? Math.floor(n) : POSTER_DEFAULT_TIME_SEC;
}

function muxThumb(playbackId: string, sec: number, width: number): string {
  return `https://image.mux.com/${encodeURIComponent(playbackId)}/thumbnail.jpg?time=${Math.max(0, Math.floor(sec))}&width=${width}`;
}

const HLS_CDN = 'https://cdn.jsdelivr.net/npm/hls.js@1.6.16/dist/hls.min.js';
const POLL_MS = 5000;

type HlsInstance = { loadSource: (u: string) => void; attachMedia: (v: HTMLVideoElement) => void; destroy: () => void };
type HlsCtor = (new (config?: unknown) => HlsInstance) & { isSupported: () => boolean };

let hlsLoadPromise: Promise<HlsCtor | null> | null = null;
function loadHls(): Promise<HlsCtor | null> {
  if (hlsLoadPromise) return hlsLoadPromise;
  hlsLoadPromise = new Promise<HlsCtor | null>((resolve) => {
    const w = window as unknown as { Hls?: HlsCtor };
    if (w.Hls) {
      resolve(w.Hls);
      return;
    }
    const script = document.createElement('script');
    script.src = HLS_CDN;
    script.async = true;
    script.onload = () => resolve((window as unknown as { Hls?: HlsCtor }).Hls ?? null);
    script.onerror = () => resolve(null);
    document.head.appendChild(script);
  });
  return hlsLoadPromise;
}

/** `YYYY-MM-DD HH:mm:ss` (Berliner Wandzeit) → `TT.MM.JJJJ HH:MM` — reine Zeichenkette, keine Zeitzone. */
function formatBerlin(s: string | null | undefined): string {
  if (!s) return '–';
  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/.exec(s);
  return m ? `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]} Uhr` : s;
}

function formatDuration(sec: number | null | undefined): string {
  if (!sec || sec <= 0) return '–';
  const total = Math.round(sec);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0
    ? `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')} h`
    : `${m}:${String(s).padStart(2, '0')} min`;
}

function copyToClipboard(text: string): void {
  navigator.clipboard?.writeText(text).catch(() => undefined);
}

const escHtml = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

/**
 * Links als VERLINKTEN Text in die Zwischenablage (HTML + Klartext): eingefügt in Teams oder Outlook steht dann
 * der Name als Link da. Nackte Adressen von ~330 Zeichen kürzt Teams — der Link ist danach kaputt.
 */
function copyLinksAsText(links: Array<{ label: string; url: string }>): void {
  const html =
    links.length === 1
      ? `<a href="${escHtml(links[0].url)}">${escHtml(links[0].label)}</a>`
      : `<ul>${links.map((l) => `<li><a href="${escHtml(l.url)}">${escHtml(l.label)}</a></li>`).join('')}</ul>`;
  const plain = links.map((l) => `${l.label}\n${l.url}`).join('\n\n');
  try {
    if (navigator.clipboard?.write && typeof ClipboardItem !== 'undefined') {
      navigator.clipboard
        .write([
          new ClipboardItem({
            'text/html': new Blob([html], { type: 'text/html' }),
            'text/plain': new Blob([plain], { type: 'text/plain' }),
          }),
        ])
        .catch(() => copyToClipboard(plain));
      return;
    }
  } catch {
    // Rückfall unten
  }
  copyToClipboard(plain);
}

/** Sendeposition als `m:ss` bzw. `−m:ss` (vor dem Start). */
function formatPos(sec: number): string {
  const neg = sec < 0;
  const total = Math.abs(Math.round(sec));
  const s = `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
  return neg ? `−${s}` : s;
}

function formatUntil(ms: number): string {
  return new Date(ms).toLocaleString('de-DE', {
    timeZone: 'Europe/Berlin',
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Grenzen wie `probeStartMs` (node-app roomTime.ts): 3 h vor dem Start bis 5 min nach dem Ende. */
const PROBE_JOIN_MIN_SEC = -3 * 3600;
const PROBE_JOIN_AFTER_END_SEC = 300;

interface ProbePreset {
  key: string;
  label: string;
  /** Sendeposition beim Öffnen; null = gesperrt (Grund in `blocked`). */
  joinAtSec: number | null;
  hint: string;
  blocked: string | null;
}

interface ProbeResult {
  label: string;
  url: string;
  expiresMs: number;
  hasCode: boolean;
  codeWanted: boolean;
  popupBlocked: boolean;
}

/** Team-Testlink „Aufzeichnung im Raum" (Server: onDemand.odTestScenarios). */
interface OdTestLink {
  key: string;
  label: string;
  arm: 'R' | 'S';
  url: string;
  /** Kurzlink zum Weitergeben (Teams kürzt den langen Link samt Linkziel). */
  short_url?: string | null;
}

/** Zum Weitergeben immer der Kurzlink, wenn es einen gibt. */
const teilbarerLink = (l: OdTestLink) => ({ label: l.label, url: l.short_url || l.url });
/** Ein Linksatz fürs Team: jedes Öffnen eines Links ist ein eigener Testlauf, Tester stören sich nicht. */
interface OdTestSet {
  nr: number;
  links: OdTestLink[];
}
interface OdTestResult {
  sets: OdTestSet[];
  expiresMs: number;
  email: string;
}

/** Ziel eines Sprungs aus dem Webinar-Fahrplan (DOM-Id `raum-<anker>`). */
export type RaumAnker = 'video' | 'angebot' | 'automatisch';

/** Felder je Abschnitt — gespeichert wird je Abschnitt, und nur Geändertes. */
const ANGEBOT_KEYS: Array<keyof FormState> = [
  'offer_enabled',
  'offer_from_minute',
  'offer_title',
  'offer_subline',
  'offer_text',
  'offer_bonus_text',
  'offer_bonus_until',
  'offer_button_label',
  'offer_image_url',
  'offer_image_pos',
  'offer_bullets',
  'offer_highlight',
];
const POSTER_KEYS: Array<keyof FormState> = ['poster_time_sec', 'poster_image_url'];
const TECHNIK_KEYS: Array<keyof FormState> = [
  'offer_order_url',
  'offer_order_url_h',
  'purchase_ab_percent',
  'offer_mode',
  'plx_campaign_id',
  'recorded_on',
  'doi_headline',
  'doi_subline',
  'doi_image_url',
];

/** Schlüssel, deren Formularwert vom geladenen Stand abweicht. */
function geaendert(keys: Array<keyof FormState>, form: FormState, saved: FormState): Array<keyof FormState> {
  return keys.filter((k) => form[k] !== saved[k]);
}

/** Nutzlast für den PUT — dieselbe Form wie bisher (Bildposition „top“ = leer). */
function nutzlast(keys: Array<keyof FormState>, form: FormState): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of keys) {
    out[k] = k === 'offer_image_pos' ? (form.offer_image_pos === 'right' ? 'right' : '') : form[k];
  }
  return out;
}

/** Nach dem Speichern eines Abschnitts NUR dessen Felder aus der Antwort übernehmen — der Rest bleibt, wie getippt. */
function uebernimm(f: FormState, frisch: FormState, keys: Array<keyof FormState>): FormState {
  const n: Record<string, unknown> = { ...f };
  for (const k of keys) n[k] = frisch[k];
  return n as unknown as FormState;
}

/** Klebende Leiste am Abschnittsende: zeigt ungespeicherte Änderungen und den Speichern-Knopf des Abschnitts. */
const SpeichernLeiste: React.FC<{ dirty: boolean; laeuft: boolean; gesperrt: boolean; onSpeichern: () => void }> = ({
  dirty,
  laeuft,
  gesperrt,
  onSpeichern,
}) => (
  <Box
    sx={{
      position: 'sticky',
      bottom: 0,
      zIndex: 2,
      mt: 2,
      mx: -2,
      mb: -2,
      px: 2,
      py: 1.25,
      display: 'flex',
      alignItems: 'center',
      gap: 2,
      flexWrap: 'wrap',
      bgcolor: dirty ? '#fff8e6' : '#ffffff',
      borderTop: `1px solid ${dirty ? '#ffd8a8' : '#e5e7eb'}`,
      borderRadius: '0 0 6px 6px',
    }}
  >
    <Button variant="contained" onClick={onSpeichern} disabled={!dirty || laeuft || gesperrt}>
      {laeuft ? 'Speichert …' : 'Abschnitt speichern'}
    </Button>
    <Typography variant="body2" color={dirty ? '#8a4b00' : 'text.secondary'}>
      {dirty ? 'Ungespeicherte Änderungen in diesem Abschnitt.' : 'Alles gespeichert.'}
    </Typography>
  </Box>
);

/** Zugeklappter Unterbereich ohne Kennzeichen (z. B. „Aufzeichnung im Raum testen“). */
const Eingeklappt: React.FC<{ titel: string; erklaerung?: string; children: React.ReactNode }> = ({
  titel,
  erklaerung,
  children,
}) => (
  <Accordion disableGutters elevation={0} sx={{ border: '1px solid #e5e7eb', '&:before': { display: 'none' } }}>
    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
      <Box>
        <Typography variant="subtitle2">{titel}</Typography>
        {erklaerung && (
          <Typography variant="caption" color="text.secondary">
            {erklaerung}
          </Typography>
        )}
      </Box>
    </AccordionSummary>
    <AccordionDetails>{children}</AccordionDetails>
  </Accordion>
);

const VIDEO_STATUS_TEXT: Record<RoomConfig['video_status'], string> = {
  none: 'Noch kein Video. Bitte hochladen — ohne Video bleibt der Raum geschlossen.',
  uploading: 'Video wird hochgeladen …',
  preparing: 'Video wird verarbeitet — dauert ein paar Minuten, der Stand aktualisiert sich von selbst.',
  ready: 'Video ist fertig.',
  errored: 'Bei der Verarbeitung des Videos ist ein Fehler aufgetreten.',
};

const UNTERTITEL_TEXT: Record<RoomConfig['captions_status'], string> = {
  none: 'Untertitel: noch keine.',
  preparing: 'Untertitel werden automatisch erzeugt (dauert etwa ein Zehntel der Videolänge).',
  ready: 'Untertitel sind fertig — Zuschauer schalten sie über das CC-Symbol ein.',
  errored: 'Untertitel konnten nicht erzeugt werden. Das Video läuft trotzdem.',
};

interface MedienBild {
  url: string;
  label: string;
}

const RoomTab: React.FC<{ webinarId?: string | null; anker?: RaumAnker }> = ({ webinarId, anker }) => {
  const istAdmin = useIstAdmin();
  const [view, setView] = useState<RoomView | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingSection, setSavingSection] = useState<string | null>(null);
  // Sperren nur nach Rückfrage mit Tipp-Bestätigung; Entsperren mit einfacher Rückfrage.
  const [sperrDialog, setSperrDialog] = useState(false);
  const [sperrFehler, setSperrFehler] = useState<string | null>(null);
  const [entsperrDialog, setEntsperrDialog] = useState(false);
  const [message, setMessage] = useState<{ text: string; severity: 'success' | 'error' | 'info' } | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [videoUrl, setVideoUrl] = useState('');
  const [probeMinuteX, setProbeMinuteX] = useState('10');
  const [probeLayout, setProbeLayout] = useState<'studio' | 'box'>('studio');
  const [probeWithCode, setProbeWithCode] = useState(true);
  const [probeDebug, setProbeDebug] = useState(false);
  const [probeBusy, setProbeBusy] = useState<string | null>(null);
  const [probeResult, setProbeResult] = useState<ProbeResult | null>(null);
  const [lookupEmail, setLookupEmail] = useState('');
  // Team-Testlinks „Aufzeichnung im Raum" (ohne Schalter, ohne Spur im Test).
  const [odTest, setOdTest] = useState<OdTestResult | null>(null);
  const [odTestBusy, setOdTestBusy] = useState(false);
  const [odTestError, setOdTestError] = useState<string | null>(null);
  const [personalLink, setPersonalLink] = useState<string | null>(null);
  // Produktbilder aus den Produkt-Medien des Webinar-Produkts (nur lesen; Auswahl schreibt nur ins Feld).
  const [medien, setMedien] = useState<{ bilder: MedienBild[]; produkt: string | null; hinweis: string | null } | null>(
    null,
  );
  const [preisLaeuft, setPreisLaeuft] = useState(false);
  // Bewerbungsstatus aus derselben Quelle wie Fahrplan/Checkliste (rein lesend); null = unbekannt.
  const [bewerbbar, setBewerbbar] = useState<boolean | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<HlsInstance | null>(null);
  // Welcher Datensatz gerade geladen ist — eine spaete Antwort fuer ein vorher gewaehltes Webinar
  // darf den Reiter nicht ueberschreiben (Async-Race).
  const loadKeyRef = useRef<string | null>(null);
  // Vorab geöffneter Tab einer laufenden Probe-Anfrage — beim Webinarwechsel schliessen, sonst
  // bekaeme der neue Datensatz einen leeren Tab bzw. den Link des alten.
  const probeTabRef = useRef<Window | null>(null);
  // Sprung aus dem Fahrplan: einmal je Webinar+Anker, erst wenn der Inhalt steht.
  const geblaettertRef = useRef<string | null>(null);

  const doiKarte = useDoiKarte(webinarId);

  // Feldprüfung (Regeln sofort, KI beim Verlassen) — Hinweis am Feld, blockiert nie.
  const pruefTitel = useFeldpruefung('raum.offer_title', webinarId);
  const pruefUnterzeile = useFeldpruefung('raum.offer_subline', webinarId);
  const pruefPreis = useFeldpruefung('raum.offer_text', webinarId);
  const pruefPunkte = useFeldpruefung('raum.offer_bullets', webinarId);
  const pruefKennzahl = useFeldpruefung('raum.offer_highlight', webinarId);
  const resetTitel = pruefTitel.zuruecksetzen;
  const resetUnterzeile = pruefUnterzeile.zuruecksetzen;
  const resetPreis = pruefPreis.zuruecksetzen;
  const resetPunkte = pruefPunkte.zuruecksetzen;
  const resetKennzahl = pruefKennzahl.zuruecksetzen;

  const load = useCallback(
    async (initForm: boolean) => {
      if (!webinarId) return;
      const key = webinarId;
      if (initForm) setLoading(true);
      const r = await fetchData<RoomView>(`/api/webinar-room-admin/${encodeURIComponent(webinarId)}`);
      if (loadKeyRef.current !== key) return;
      if (initForm) setLoading(false);
      if (!r.success || !r.data) {
        setMessage({ text: r.error || 'Raum konnte nicht geladen werden', severity: 'error' });
        return;
      }
      setView(r.data);
      if (initForm) setForm(formFromConfig(r.data.config));
    },
    [webinarId],
  );

  // Datensatzwechsel: alles zuruecksetzen, dann frisch laden.
  useEffect(() => {
    loadKeyRef.current = webinarId || null;
    setView(null);
    setForm(EMPTY_FORM);
    setMessage(null);
    setSavingSection(null);
    setSperrDialog(false);
    setSperrFehler(null);
    setEntsperrDialog(false);
    setProbeResult(null);
    setProbeBusy(null);
    probeTabRef.current?.close();
    probeTabRef.current = null;
    setPersonalLink(null);
    setOdTest(null);
    setOdTestBusy(false);
    setOdTestError(null);
    setVideoUrl('');
    setLookupEmail('');
    setMedien(null);
    setPreisLaeuft(false);
    setBewerbbar(null);
    resetTitel();
    resetUnterzeile();
    resetPreis();
    resetPunkte();
    resetKennzahl();
    if (fileInputRef.current) fileInputRef.current.value = '';
    load(true);
  }, [webinarId, load, resetTitel, resetUnterzeile, resetPreis, resetPunkte, resetKennzahl]);

  // Produkt-Medien des Webinar-Produkts (rein lesend). Fehlt etwas, bleibt das Adressfeld der Weg.
  useEffect(() => {
    if (!webinarId) return;
    let cancelled = false;
    ladeProduktMedien(webinarId).then((r) => {
      if (cancelled) return;
      if (!r.success || !r.data) {
        setMedien({ bilder: [], produkt: null, hinweis: 'Die Produkt-Medien konnten nicht geladen werden.' });
        return;
      }
      const d = r.data;
      const bilder: MedienBild[] = [];
      for (const f of d.faecher) {
        const urls = f.cover?.urls;
        if (!urls) continue;
        for (const v of PRODUKT_VARIANTEN) {
          const u = urls[v];
          if (u && /^https:\/\//.test(u)) {
            bilder.push({ url: u, label: `${PRODUKT_SLOT_LABEL[f.slot]} · ${PRODUKT_VARIANTE_LABEL[v]}` });
          }
        }
      }
      setMedien({
        bilder,
        produkt: d.produktName || d.plxProduct,
        hinweis: !d.plxProduct
          ? 'Dem Webinar ist kein Produkt zugeordnet — deshalb gibt es keine Produkt-Medien zur Auswahl.'
          : bilder.length === 0
            ? 'Für dieses Produkt sind noch keine Produkt-Medien hinterlegt (Bestellstrecke → Produkt-Medien).'
            : null,
      });
    });
    return () => {
      cancelled = true;
    };
  }, [webinarId]);

  // Bewerbungsstatus (GET promotion-status, dieselbe Rechnung wie das Gate) — nur fuer den Satz unter dem
  // Portal-Werbung-Schalter, damit „erlaubt“ nicht als „wird beworben“ gelesen wird.
  useEffect(() => {
    if (!webinarId) return;
    let cancelled = false;
    fetchData<{ status: string }>(
      `/api/webinar-management/webinars/${encodeURIComponent(webinarId)}/promotion-status`,
    ).then((r) => {
      if (!cancelled) setBewerbbar(r.success && r.data ? r.data.status === 'bewerbbar' : null);
    });
    return () => {
      cancelled = true;
    };
  }, [webinarId]);

  // Sprung aus dem Webinar-Fahrplan, einmal nach dem ersten Laden. Merker erst IM Timer setzen: StrictMode
  // ruft den Effekt doppelt, der Aufräumzweig löscht den ersten Timer.
  useEffect(() => {
    if (!anker || !view || !webinarId) return;
    const k = `${webinarId}:${anker}`;
    if (geblaettertRef.current === k) return;
    const t = setTimeout(() => {
      geblaettertRef.current = k;
      document.getElementById(`raum-${anker}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
    return () => clearTimeout(t);
  }, [anker, view, webinarId]);

  const cfg = view?.config || null;
  // Auch bei ready + Untertitel 'none' weiter abfragen: jeder Upload bestellt Auto-Untertitel, und
  // erscheint die Spur bei Mux erst nach „ready", stuende der Reiter sonst dauerhaft auf „keine".
  const processing =
    uploadProgress !== null ||
    cfg?.video_status === 'uploading' ||
    cfg?.video_status === 'preparing' ||
    (cfg?.video_status === 'ready' &&
      (cfg?.captions_status === 'preparing' || cfg?.captions_status === 'none'));

  useEffect(() => {
    if (!processing) return;
    const id = setInterval(() => load(false), POLL_MS);
    return () => clearInterval(id);
  }, [processing, load]);

  // Warnung beim Schliessen waehrend eines Uploads: UpChunk setzt nach einem Reload NICHT fort.
  useEffect(() => {
    if (uploadProgress === null) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [uploadProgress]);

  // Vorschau: nur das fertige Video, eigene hls.js-Instanz, Aufraeumen bei Wechsel.
  const previewUrl = view?.preview_hls_url || null;
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!previewUrl || !videoRef.current) return;
      const Hls = await loadHls();
      if (cancelled || !videoRef.current) return;
      const video = videoRef.current;
      if (Hls && Hls.isSupported()) {
        const hls = new Hls();
        hls.loadSource(previewUrl);
        hls.attachMedia(video);
        hlsRef.current = hls;
      } else {
        video.src = previewUrl;
      }
    })();
    return () => {
      cancelled = true;
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [previewUrl]);

  const handleFilePicked = async (file: File) => {
    if (!webinarId) return;
    setMessage(null);
    setUploadProgress(0);
    let uploadId: string | null = null;
    try {
      const res = await api.post<{ success: boolean; data?: { upload_url: string; mux_upload_id: string }; error?: string }>(
        `/api/webinar-room-admin/${encodeURIComponent(webinarId)}/upload-url`,
        { cors_origin: window.location.origin },
      );
      const uploadUrl = res.data?.data?.upload_url;
      uploadId = res.data?.data?.mux_upload_id || null;
      if (!uploadUrl) throw new Error(res.data?.error || 'Keine Upload-Adresse erhalten');
      await load(false);
      await new Promise<void>((resolve, reject) => {
        const upload = UpChunk.createUpload({ endpoint: uploadUrl, file, dynamicChunkSize: true });
        upload.on('progress', (evt: { detail: number }) => setUploadProgress(Math.round(evt.detail)));
        upload.on('success', () => resolve());
        upload.on('error', (evt: { detail?: { message?: string } }) => {
          const msg = evt?.detail?.message || 'Upload fehlgeschlagen';
          if (uploadId) {
            api
              .post(`/api/webinar-room-admin/${encodeURIComponent(webinarId)}/upload-failed`, {
                mux_upload_id: uploadId,
                reason: msg,
              })
              .catch(() => undefined);
          }
          reject(new Error(msg));
        });
      });
      setMessage({ text: 'Upload fertig — das Video wird jetzt verarbeitet, danach entstehen die Untertitel.', severity: 'success' });
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      setMessage({ text: e?.response?.data?.error || e?.message || 'Upload fehlgeschlagen', severity: 'error' });
    } finally {
      setUploadProgress(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await load(false);
    }
  };

  const handleUrlImport = async () => {
    if (!webinarId || !videoUrl.trim()) return;
    setSaving(true);
    setMessage(null);
    const r = await fetchData<{ mux_asset_id: string }>(
      `/api/webinar-room-admin/${encodeURIComponent(webinarId)}/video-url`,
      { method: 'POST', body: JSON.stringify({ url: videoUrl.trim() }) },
    );
    setSaving(false);
    if (!r.success) {
      setMessage({ text: r.error || 'Import fehlgeschlagen', severity: 'error' });
      return;
    }
    setVideoUrl('');
    setMessage({ text: 'Die Datei wird abgeholt — der Stand aktualisiert sich von selbst.', severity: 'success' });
    await load(false);
  };

  /** Gleicher Request wie der frühere Schalter (`room_enabled`). `true` = geklappt. */
  const toggleRoom = async (enabled: boolean): Promise<boolean> => {
    if (!webinarId) return false;
    const key = webinarId;
    setSaving(true);
    setMessage(null);
    const r = await fetchData<RoomView>(`/api/webinar-room-admin/${encodeURIComponent(webinarId)}`, {
      method: 'PUT',
      body: JSON.stringify({ room_enabled: enabled }),
    });
    if (loadKeyRef.current !== key) return false;
    setSaving(false);
    if (!r.success || !r.data) {
      const text = r.error || 'Umschalten fehlgeschlagen';
      setMessage({ text, severity: 'error' });
      setSperrFehler(text);
      return false;
    }
    setView(r.data);
    setMessage({ text: enabled ? 'Raum ist entsperrt.' : 'Raum ist gesperrt.', severity: 'success' });
    return true;
  };

  const togglePromo = async (enabled: boolean) => {
    if (!webinarId) return;
    setSaving(true);
    setMessage(null);
    const r = await fetchData<RoomView>(`/api/webinar-room-admin/${encodeURIComponent(webinarId)}`, {
      method: 'PUT',
      body: JSON.stringify({ promo_enabled: enabled }),
    });
    setSaving(false);
    if (!r.success || !r.data) {
      setMessage({ text: r.error || 'Umschalten fehlgeschlagen', severity: 'error' });
      return;
    }
    setView(r.data);
    setMessage({
      text: enabled ? 'Webinar wird am Webinartag auf den Portalen beworben.' : 'Keine Werbung auf den Portalen.',
      severity: 'success',
    });
  };

  /**
   * Speichert EINEN Abschnitt: schickt nur dessen geänderte Felder (der Server nimmt Teilmengen) und übernimmt
   * danach nur dessen Felder aus der Antwort — ungespeicherte Eingaben anderer Abschnitte bleiben stehen.
   */
  const saveSection = async (bereich: string, keys: Array<keyof FormState>) => {
    if (!webinarId || savingSection || saving) return;
    const key = webinarId;
    const changed = geaendert(keys, form, formFromConfig(view?.config ?? null));
    if (changed.length === 0) return;
    setSavingSection(bereich);
    setMessage(null);
    const r = await fetchData<RoomView>(`/api/webinar-room-admin/${encodeURIComponent(webinarId)}`, {
      method: 'PUT',
      body: JSON.stringify(nutzlast(changed, form)),
    });
    if (loadKeyRef.current !== key) return;
    setSavingSection(null);
    if (!r.success || !r.data) {
      setMessage({ text: r.error || 'Speichern fehlgeschlagen', severity: 'error' });
      return;
    }
    setView(r.data);
    const frisch = formFromConfig(r.data.config);
    setForm((f) => uebernimm(f, frisch, keys));
    setMessage({ text: `${bereich}: gespeichert.`, severity: 'success' });
  };

  /**
   * Preiszeile der Bestellseite DIESES Webinars ins Feld (nicht speichern) — nur, wenn sie genau so live steht
   * (Server-Riegel `livePreisDerBestellseite`, Codex-Review 22.10.). Abgelehnt → Feld bleibt, wie es ist.
   */
  const preisAusBestellseite = async () => {
    if (!webinarId) return;
    const key = webinarId;
    setPreisLaeuft(true);
    const r = await getLivePreis(webinarId);
    if (loadKeyRef.current !== key) return;
    setPreisLaeuft(false);
    const preis = r.success && r.data && !r.data.grund ? (r.data.preis || '').trim() : '';
    if (!preis) {
      setMessage({
        text: !r.success
          ? r.error || 'Die Bestellseite konnte nicht geladen werden.'
          : LIVE_PREIS_HINWEIS[r.data?.grund ?? 'kein_preis'],
        severity: 'info',
      });
      return;
    }
    setForm((f) => ({ ...f, offer_text: preis }));
    void pruefPreis.pruefen(preis, { mitKi: true });
    setMessage({ text: 'Preis aus der Bestellseite eingetragen — bitte prüfen und den Abschnitt speichern.', severity: 'info' });
  };

  // Ein Klick = ein frischer Probe-Link in einem neuen Tab. Der Tab wird SYNCHRON im Klick geöffnet
  // (sonst greift der Pop-up-Blocker, weil die Antwort erst nach dem await kommt) und bekommt die
  // Adresse, sobald der Server den Link signiert hat. Die Uhr der Probe läuft ab diesem Klick.
  const openProbe = async (preset: ProbePreset) => {
    if (!webinarId || preset.joinAtSec === null || probeBusy) return;
    const key = webinarId;
    const tab = window.open('about:blank', '_blank');
    if (tab) {
      tab.opener = null;
      try {
        tab.document.title = 'Test-Link wird vorbereitet …';
        tab.document.body.textContent = 'Test-Link wird erzeugt …';
      } catch {
        // Nur Kosmetik — der Tab bekommt gleich die echte Adresse.
      }
    }
    probeTabRef.current = tab;
    setProbeBusy(preset.key);
    setProbeResult(null);
    const r = await fetchData<{ url: string; expires_ms: number; has_code: boolean }>(
      `/api/webinar-room-admin/${encodeURIComponent(webinarId)}/probe`,
      {
        method: 'POST',
        // Debug-Anzeige gibt es nur für den Admin (Schalter sonst unsichtbar, also immer aus).
        body: JSON.stringify({ join_at_sec: preset.joinAtSec, with_code: probeWithCode, debug: istAdmin && probeDebug }),
      },
    );
    if (loadKeyRef.current !== key) {
      // Inzwischen ein anderes Webinar gewählt: der Reset hat den Tab schon geschlossen.
      tab?.close();
      return;
    }
    probeTabRef.current = null;
    setProbeBusy(null);
    if (!r.success || !r.data) {
      tab?.close();
      setMessage({ text: r.error || 'Test-Link fehlgeschlagen', severity: 'error' });
      return;
    }
    let url = r.data.url;
    if (probeLayout === 'box') {
      const u = new URL(url);
      u.searchParams.set('layout', '2');
      url = u.toString();
    }
    const opened = Boolean(tab && !tab.closed);
    if (tab && opened) tab.location.href = url;
    setProbeResult({
      label: preset.label,
      url,
      expiresMs: r.data.expires_ms,
      hasCode: r.data.has_code,
      codeWanted: probeWithCode,
      popupBlocked: !opened,
    });
  };

  const lookupPersonalLink = async () => {
    if (!webinarId || !lookupEmail.trim()) return;
    setPersonalLink(null);
    const r = await fetchData<{ url: string; has_code: boolean }>(
      `/api/webinar-room-admin/${encodeURIComponent(webinarId)}/personal-link?email=${encodeURIComponent(lookupEmail.trim())}`,
    );
    if (!r.success || !r.data) {
      setMessage({ text: r.error || 'Keine Anmeldung gefunden', severity: 'error' });
      return;
    }
    setPersonalLink(r.data.url);
  };

  // Ohne Adresse im Feld nimmt der Server die eingeloggte — beide brauchen eine Anmeldung für DIESES Webinar.
  const loadOdTestLinks = async () => {
    if (!webinarId) return;
    const key = loadKeyRef.current;
    setOdTest(null);
    setOdTestError(null);
    setOdTestBusy(true);
    // Ein Linksatz reicht fuers Team: jedes Oeffnen bekommt serverseitig eine eigene Testzeile (onDemand.odTestRunSeat).
    const r = await fetchData<{ sets: OdTestSet[]; expires_ms: number; email: string }>(
      `/api/webinar-room-admin/${encodeURIComponent(webinarId)}/od-test-links`,
      {
        method: 'POST',
        body: JSON.stringify(lookupEmail.trim() ? { email: lookupEmail.trim() } : {}),
      },
    );
    if (loadKeyRef.current !== key) return;
    setOdTestBusy(false);
    if (!r.success || !r.data) {
      // Meldung direkt am Knopf — die allgemeine Meldung steht oben im Reiter und wäre hier unten nicht zu sehen.
      setOdTestError(r.error || 'Testlinks konnten nicht erzeugt werden.');
      return;
    }
    setOdTest({ sets: r.data.sets, expiresMs: r.data.expires_ms, email: r.data.email });
  };

  // Meldungen unten eingeblendet: der Reiter ist lang, eine Meldung oben sähe nach dem Speichern unten niemand.
  const meldung = (
    <Snackbar
      open={!!message}
      autoHideDuration={message?.severity === 'error' ? null : 6000}
      onClose={(_, reason) => {
        if (reason !== 'clickaway') setMessage(null);
      }}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      {message ? (
        <Alert severity={message.severity} onClose={() => setMessage(null)} variant="filled" sx={{ maxWidth: 720 }}>
          {message.text}
        </Alert>
      ) : (
        <span />
      )}
    </Snackbar>
  );

  if (!webinarId) {
    return (
      <Alert severity="info">
        Bitte in der Webinarliste beim gewünschten Webinar auf das Raum-Symbol klicken.
      </Alert>
    );
  }
  if (loading || !view) {
    if (!loading && message?.severity === 'error') return <Alert severity="error">{message.text}</Alert>;
    return (
      <Box display="flex" alignItems="center" gap={1} p={2}>
        <CircularProgress size={20} /> <Typography>Raum wird geladen …</Typography>
      </Box>
    );
  }

  const w = view.webinar;
  const videoReady = cfg?.video_status === 'ready';
  // Ohne Config-Zeile gilt der Spaltenstandard „an“ (Migration 20261008, die Zeile entsteht beim ersten Upload/
  // Speichern mit room_enabled = 1). Bis 22.10. stand hier „aus“ — der Reiter meldete „per Not-Aus ausgeschaltet“
  // für jedes neue Webinar, waehrend das Gate denselben Fall richtig als „Video fehlt“ wertet.
  const roomOn = cfg == null ? true : Number(cfg.room_enabled) === 1;
  // Ohne Config-Zeile oder Spalte gilt der Standard: bewerben.
  const promoOn = cfg?.promo_enabled == null ? true : Number(cfg.promo_enabled) === 1;
  const unsupported = w.is_evergreen || w.is_recurring;
  const saved = formFromConfig(cfg);
  const dirtyAngebot = geaendert(ANGEBOT_KEYS, form, saved).length > 0;
  const dirtyPoster = geaendert(POSTER_KEYS, form, saved).length > 0;
  const dirtyTechnik = geaendert(TECHNIK_KEYS, form, saved).length > 0;
  const setF = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm((f) => ({ ...f, [k]: v }));

  const posterPreview = form.poster_image_url.trim()
    ? form.poster_image_url.trim()
    : cfg?.mux_playback_id
      ? muxThumb(cfg.mux_playback_id, posterSecOf(form.poster_time_sec), 640)
      : null;

  // Test-Zeitpunkte: alle aus dem GESPEICHERTEN Stand (den liest auch der Raum), nicht aus dem Formular.
  const durationSec = Number(cfg?.video_duration_sec) || 0;
  // Spielbar = dieselbe Bedingung wie der Einlass (Video ready, Playback, Dauer). An + nicht spielbar = wie aus.
  const roomPlayable = videoReady && !!cfg?.mux_playback_id && durationSec > 0;
  const globalBlock =
    !videoReady || !durationSec
      ? 'Erst möglich, wenn das Video fertig verarbeitet ist.'
      : unsupported
        ? 'Evergreen- und Wiederholungs-Webinare zeigt der Raum nicht.'
        : null;
  const offerSec = cfg?.offer_from_minute == null ? null : Number(cfg.offer_from_minute) * 60;
  // Wie room.html offerActive: ohne Bestell-Link und ohne Einbettung erscheint das Angebot nie.
  const hasOrderPath =
    Boolean(view.order_link_preview) || (cfg?.offer_mode === 'embed' && Boolean(cfg?.plx_campaign_id));
  const offerBlocked =
    Number(cfg?.offer_enabled) !== 1
      ? 'Angebot ist ausgeschaltet (gespeicherter Stand).'
      : offerSec === null || !Number.isFinite(offerSec)
        ? 'Keine Angebotsminute gespeichert.'
        : !hasOrderPath
          ? 'Der Kaufknopf hat kein Ziel — das Angebot erscheint nicht.'
          : offerSec >= durationSec
            ? 'Die Angebotsminute liegt hinter dem Videoende.'
            : null;
  const minuteXNum = Number(probeMinuteX.replace(',', '.'));
  const minuteXSec = probeMinuteX.trim() !== '' && Number.isFinite(minuteXNum) ? Math.round(minuteXNum * 60) : null;
  const minuteXMax = Math.floor((durationSec + PROBE_JOIN_AFTER_END_SEC) / 60);
  const minuteXBlocked =
    minuteXSec === null || minuteXSec < PROBE_JOIN_MIN_SEC || minuteXSec > durationSec + PROBE_JOIN_AFTER_END_SEC
      ? `Erlaubt: −180 bis ${minuteXMax}`
      : null;
  const preset = (key: string, label: string, joinAtSec: number, hint: string, blocked: string | null = null): ProbePreset => {
    const reason = globalBlock || blocked;
    return { key, label, joinAtSec: reason ? null : joinAtSec, hint, blocked: reason };
  };
  const probePresets: ProbePreset[] = [
    preset('wait', 'Warteraum', -120, 'Einstieg 2 Min vor dem Start: Countdown und Warteraum.'),
    preset('start', 'Sendestart', -30, 'Einstieg 30 s vor dem Start: Übergang Warteraum → Sendung.'),
    preset(
      'minute',
      `Minute ${probeMinuteX.trim() || 'X'}`,
      minuteXSec ?? 0,
      `Einstieg bei ${formatPos(minuteXSec ?? 0)} — Späteinstieg, negativ = vor dem Start.`,
      minuteXBlocked,
    ),
    preset(
      'preOffer',
      '1 Min vor Angebot',
      (offerSec ?? 0) - 60,
      `Einstieg bei ${formatPos((offerSec ?? 0) - 60)}: das Angebot erscheint nach ~60 s.`,
      offerBlocked,
    ),
    preset(
      'offer',
      'Angebot erscheint',
      (offerSec ?? 0) - 10,
      `Einstieg bei ${formatPos((offerSec ?? 0) - 10)}: das Angebot erscheint nach ~10 s.`,
      offerBlocked,
    ),
    preset(
      'last',
      'Letzte Minuten → nach dem Video',
      Math.floor(durationSec) - 90,
      `Einstieg bei ${formatPos(Math.floor(durationSec) - 90)}, 90 s vor dem Ende: danach die Kaufkarte nach dem Video (für alle, die mindestens 30 s geschaut haben).`,
    ),
    preset(
      'after',
      'Nach dem Ende',
      Math.ceil(durationSec) + 5,
      view.replay_redirect_preview
        ? 'Einstieg 5 s nach dem Ende, 0 s geschaut: Weiterleitung auf die Aufzeichnungsseite.'
        : 'Einstieg 5 s nach dem Ende, 0 s geschaut: keine Aufzeichnungsseite hinterlegt — der Raum zeigt einen Hinweis.',
    ),
  ];

  // Wohin der Kaufknopf HEUTE führt (gespeicherter Stand, dieselbe Reihenfolge wie buildOrderLink:
  // eigene Raum-URL → Bestell-URL des Webinars).
  const kaufZiel = cfg?.offer_order_url || w.order_url || null;
  const kaufQuelle = cfg?.offer_order_url
    ? 'eigene Bestell-Adresse des Raums (vom Admin unter „Technik“ eingetragen)'
    : w.order_url && /lp\.finanztrends\.de\/w\//.test(w.order_url)
      ? 'eigene Bestellstrecke dieses Webinars (live geschaltet)'
      : w.order_url
        ? 'Bestell-Adresse des Webinars'
        : null;
  const imEmbed = cfg?.offer_mode === 'embed' && Boolean(cfg?.plx_campaign_id);

  // Angebotsstatus in Worten (gespeicherter Stand — den liest der Raum).
  const angebotStatus: { severity: 'success' | 'info' | 'warning'; text: string } =
    Number(cfg?.offer_enabled) !== 1
      ? { severity: 'info', text: 'Aus — im Webinar gibt es derzeit kein Kaufangebot.' }
      : !hasOrderPath
        ? {
            severity: 'warning',
            text: 'An, aber der Kaufknopf hat noch kein Ziel — das Angebot erscheint deshalb nicht. Erst die Bestellstrecke live schalten.',
          }
        : offerSec === null
          ? {
              severity: 'warning',
              text: 'An, aber ohne Minute: erscheint erst nach dem Video — während der Sendung gibt es kein Angebot. Bitte „Ab Minute“ eintragen.',
            }
          : videoReady && offerSec >= durationSec
            ? {
                severity: 'warning',
                text: `An, aber Minute ${cfg?.offer_from_minute} liegt hinter dem Videoende — während der Sendung erscheint das Angebot nicht.`,
              }
            : { severity: 'success', text: `An — erscheint ab Minute ${cfg?.offer_from_minute} der Sendung und bleibt nach dem Video stehen.` };

  const termin = `${datumDe(w.start_date)}${w.start_date.length >= 16 ? `, ${w.start_date.slice(11, 16)} Uhr` : ''}`;
  const sperrWirkung = (
    <>
      Danach wird das Webinar nirgends mehr beworben (Newsletter, Anzeigen, Portal-Werbung). Echte Anmelder landen auf
      der Aufzeichnungsseite, alle anderen sehen „nicht verfügbar“ — auch in bereits geöffneten Fenstern, binnen
      Sekunden. Der Raum bleibt gesperrt, bis ihn jemand hier wieder entsperrt; keine Automatik schaltet ihn zurück.
    </>
  );

  return (
    <Box>
      {meldung}

      {/* 1. Kopf: Titel, Termin, Raum-Link, Status in Worten */}
      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#ffffff', borderColor: '#e5e7eb' }}>
        {/* Oeffentlicher Titel gross (das sehen Zuschauer im Raum), interner Arbeitstitel grau darunter. */}
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Typography variant="h5">{w.public_title || w.title || w.webinar_id}</Typography>
          {!w.public_title && <Chip size="small" color="warning" variant="outlined" label="öffentlicher Titel fehlt" />}
        </Stack>
        {w.internal_title && w.internal_title !== w.public_title && (
          <Typography variant="body2" color="text.disabled">
            intern: {w.internal_title}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          Termin {termin}
          {w.host_name ? ` · ${w.host_name}` : ''} · {w.webinar_id}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" mt={1} flexWrap="wrap">
          <Typography variant="body2">Raum-Link:</Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
            {view.room_url}
          </Typography>
          <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => copyToClipboard(view.room_url)}>
            Kopieren
          </Button>
          <Button size="small" startIcon={<OpenInNewIcon />} href={view.room_url} target="_blank" rel="noopener">
            Öffnen
          </Button>
        </Stack>
        <Box mt={1.5}>
          {!roomOn ? (
            <Alert
              severity="error"
              action={
                <Button color="inherit" size="small" onClick={() => setEntsperrDialog(true)} disabled={saving || unsupported}>
                  Entsperren …
                </Button>
              }
            >
              <strong>Raum ist gesperrt</strong>
              {cfg?.room_disabled_at ? ` seit ${formatBerlin(cfg.room_disabled_at)}` : ''}
              {cfg?.room_disabled_by ? ` von ${cfg.room_disabled_by}` : ''}. Keine Werbung; echte Anmelder landen auf der
              Aufzeichnungsseite, alle anderen sehen „nicht verfügbar“.
            </Alert>
          ) : roomPlayable ? (
            <Alert severity="success">
              <strong>Raum ist an.</strong> Angemeldete kommen zum Termin über ihren Link hinein.
            </Alert>
          ) : (
            <Alert severity="info">
              <strong>Raum ist an</strong> — wartet auf das fertige Video. Bis dahin verhält er sich wie geschlossen.
            </Alert>
          )}
        </Box>
        {unsupported && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            Der eigene Raum unterstützt nur einmalige Termine (kein Evergreen, keine Wiederholung).
          </Alert>
        )}
        {w.public_title_problem && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            {w.public_title_problem} Erst im Reiter „Webinar“ den öffentlichen Titel setzen — ohne ihn wird das Webinar
            nicht beworben und der Raum zeigt den Arbeitstitel.
          </Alert>
        )}
      </Paper>

      {/* 2. Video */}
      <Abschnitt
        id="raum-video"
        titel="Video"
        art="pflicht"
        erklaerung="Die Aufzeichnung, die im Raum zum Termin läuft. Ohne fertiges Video bleibt der Raum geschlossen."
      >
        <Alert
          severity={videoReady ? 'success' : cfg?.video_status === 'errored' ? 'error' : 'info'}
          sx={{ mb: 1.5 }}
        >
          {VIDEO_STATUS_TEXT[cfg?.video_status || 'none']}
          {videoReady && ` Länge ${formatDuration(cfg?.video_duration_sec)}.`}
          {cfg?.video_status === 'errored' && cfg.video_error && ` (${cfg.video_error}) Bitte erneut hochladen.`}
          {cfg && cfg.video_status !== 'none' && ` ${UNTERTITEL_TEXT[cfg.captions_status || 'none']}`}
        </Alert>
        {view.video_locked && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            Das Video ist gesperrt: ab 60 Minuten vor dem Start bis zum Ende der Sendung kann es nicht ersetzt werden.
          </Alert>
        )}
        {uploadProgress !== null && (
          <Box mb={1.5}>
            <Typography variant="body2">Upload läuft: {uploadProgress} % — dieses Fenster bitte geöffnet lassen.</Typography>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
          <Button
            variant="contained"
            startIcon={<UploadFileIcon />}
            disabled={uploadProgress !== null || view.video_locked}
            onClick={() => fileInputRef.current?.click()}
          >
            {cfg && cfg.video_status !== 'none' ? 'Video ersetzen (Datei)' : 'Video hochladen (Datei)'}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="video/*"
            hidden
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFilePicked(f);
            }}
          />
          <Typography variant="body2" color="text.secondary">
            oder
          </Typography>
          <TextField
            size="small"
            sx={{ minWidth: { md: 360 }, flex: 1 }}
            label="Direkte Adresse zur Videodatei (MP4/MOV, https)"
            value={videoUrl}
            onChange={(e) => setVideoUrl(e.target.value)}
            disabled={uploadProgress !== null || view.video_locked}
          />
          <Button variant="outlined" onClick={handleUrlImport} disabled={saving || !videoUrl.trim() || view.video_locked}>
            Importieren
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" mt={1}>
          Deutsche Untertitel entstehen automatisch. Ein Ersatz-Upload setzt Video und Untertitel neu.
        </Typography>
        {!videoReady && webinarId && (
          <VideoZusageKasten webinarId={webinarId} wert={cfg?.video_expected_by ?? null} onView={setView} />
        )}
        {videoReady && previewUrl && (
          <Box mt={2}>
            <Typography variant="subtitle2" gutterBottom>
              Vorschau (hier mit Spulen — im Raum ist Spulen gesperrt; Untertitel über das CC-Symbol)
            </Typography>
            <video
              ref={videoRef}
              controls
              playsInline
              crossOrigin="anonymous"
              style={{ width: '100%', maxWidth: 720, background: '#000', borderRadius: 6 }}
            />
          </Box>
        )}
      </Abschnitt>

      {/* 3. Kaufangebot */}
      <Abschnitt
        id="raum-angebot"
        titel="Kaufangebot im Webinar"
        art="pflichtVerkauf"
        erklaerung="Die Kaufkarte mit Knopf, die Zuschauer während und nach der Sendung sehen. Ohne sie wird im Webinar nichts verkauft."
      >
        <Box sx={{ maxWidth: 760 }}>
          <Box sx={{ p: 1.5, mb: 2, border: '1px solid #e5e7eb', borderRadius: 1, bgcolor: '#fcfcfd' }}>
            <FormControlLabel
              control={<Switch checked={form.offer_enabled} onChange={(e) => setF('offer_enabled', e.target.checked)} />}
              label={<strong>Angebot während der Sendung einblenden</strong>}
            />
            <Typography variant="body2" color="text.secondary">
              An: Ab der eingestellten Minute erscheint im Raum die Kaufkarte; nach dem Ende des Videos steht sie weiter da
              (für alle, die mindestens 30 Sekunden geschaut haben). Aus:
              Zuschauer sehen im Webinar kein Kaufangebot — alle Felder darunter bleiben gespeichert, wirken aber nicht.
            </Typography>
            <Alert severity={angebotStatus.severity} sx={{ mt: 1 }}>
              Gespeicherter Stand: {angebotStatus.text}
            </Alert>
            {view.recording_cta_hint && (
              <Alert severity="warning" sx={{ mt: 1 }}>
                {view.recording_cta_hint}
              </Alert>
            )}
          </Box>

          <Feld
            titel="Ab Minute"
            art="pflichtVerkauf"
            wofuer="Ab dieser Minute zeigt der Raum die Kaufkarte. Leer = erst nach dem Video (dann gibt es während der Sendung kein Angebot). Die Stelle, an der der Referent zum Angebot kommt — im Video nachsehen oder unten bei „Testen“ mit „Minute X“ prüfen."
            beispiele={{ feld: 'raum.offer_from_minute', webinarId, onUebernehmen: (v) => setF('offer_from_minute', v) }}
          >
            <TextField
              type="number"
              size="small"
              inputProps={{ min: 0, max: 600 }}
              value={form.offer_from_minute}
              onChange={(e) => setF('offer_from_minute', e.target.value)}
              placeholder="z. B. 45"
              sx={{ width: 160 }}
            />
          </Feld>

          <Feld
            titel="Titel"
            art="optional"
            wofuer="Große Überschrift der Kaufkarte. Leer = der öffentliche Webinartitel."
            beispiele={{ feld: 'raum.offer_title', webinarId, onUebernehmen: (v) => setF('offer_title', v) }}
            befunde={pruefTitel.befunde}
            pruefungLaeuft={pruefTitel.laeuft}
          >
            <TextField
              fullWidth
              size="small"
              value={form.offer_title}
              onChange={(e) => setF('offer_title', e.target.value)}
              onBlur={() => void pruefTitel.pruefen(form.offer_title, { mitKi: true })}
              placeholder={w.title ? `leer = Webinartitel „${w.title}“` : 'leer = Webinartitel'}
            />
          </Feld>

          <Feld
            titel="Unterzeile"
            art="optional"
            wofuer="Steht direkt unter dem Titel. Neue Zeile = Zeilenumbruch, **so** wird fett. Leer = keine Unterzeile."
            beispiele={{ feld: 'raum.offer_subline', webinarId, onUebernehmen: (v) => setF('offer_subline', v) }}
            befunde={pruefUnterzeile.befunde}
            pruefungLaeuft={pruefUnterzeile.laeuft}
          >
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={form.offer_subline}
              onChange={(e) => setF('offer_subline', e.target.value)}
              onBlur={() => void pruefUnterzeile.pruefen(form.offer_subline, { mitKi: true })}
              placeholder={'z. B. **Name des Produkts**\nEin Satz, was es leistet.'}
              error={form.offer_subline.length > 300}
              helperText={
                form.offer_subline.length > 300
                  ? `Höchstens 300 Zeichen (jetzt ${form.offer_subline.length}) — der Rest wird abgeschnitten.`
                  : undefined
              }
            />
          </Feld>

          <Feld
            titel="Preis und Bedingungen"
            art="pflichtVerkauf"
            wofuer="Steht auf der Kaufkarte, in der Leiste unter dem Video und in der Kaufleiste am Handy. Preis vollständig: pro Woche UND pro Monat (Pflicht nach der Preisangabenverordnung), kurz halten (höchstens 140 Zeichen)."
            beispiele={{ feld: 'raum.offer_text', webinarId, onUebernehmen: (v) => setF('offer_text', v) }}
            befunde={pruefPreis.befunde}
            pruefungLaeuft={pruefPreis.laeuft}
            aktion={
              <Button size="small" onClick={() => void preisAusBestellseite()} disabled={preisLaeuft}>
                {preisLaeuft ? 'Lädt …' : 'Preis aus der Bestellseite übernehmen'}
              </Button>
            }
          >
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              value={form.offer_text}
              onChange={(e) => setF('offer_text', e.target.value)}
              onBlur={() => void pruefPreis.pruefen(form.offer_text, { mitKi: true })}
              placeholder="z. B. 0 € für 30 Tage, danach … € pro Woche (… € pro Monat), jederzeit kündbar"
              error={form.offer_text.length > 140}
              helperText={
                form.offer_text.length > 140
                  ? `Bitte kürzer fassen (jetzt ${form.offer_text.length} Zeichen, Ziel höchstens 140).`
                  : undefined
              }
            />
          </Feld>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <Feld
              titel="Bonus-Frist"
              art="optional"
              wofuer="Nur ein echtes Bonusende nennen — der Raum zeigt keinen Countdown. Leer = kein Bonus-Hinweis."
              beispiele={{ feld: 'raum.offer_bonus_text', webinarId, onUebernehmen: (v) => setF('offer_bonus_text', v) }}
              sx={{ flex: 1 }}
            >
              <TextField
                fullWidth
                size="small"
                value={form.offer_bonus_text}
                onChange={(e) => setF('offer_bonus_text', e.target.value)}
              />
            </Feld>
            <Feld
              titel="Bonus gültig bis (Wiederholung)"
              art="optional"
              wofuer="Danach zeigt die Wiederholung den Bonus nicht mehr. Leer = in der Wiederholung kein Bonus."
              sx={{ width: { xs: '100%', sm: 280 } }}
            >
              <TextField
                fullWidth
                size="small"
                type="datetime-local"
                value={form.offer_bonus_until}
                onChange={(e) => setF('offer_bonus_until', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Feld>
          </Stack>

          <Feld
            titel="Text des Kaufknopfs"
            art="optional"
            wofuer="Beschriftung des Knopfs auf der Kaufkarte. Leer = „Jetzt bestellen“."
            beispiele={{ feld: 'raum.offer_button_label', webinarId, onUebernehmen: (v) => setF('offer_button_label', v) }}
          >
            <TextField
              fullWidth
              size="small"
              value={form.offer_button_label}
              onChange={(e) => setF('offer_button_label', e.target.value)}
              placeholder="leer = „Jetzt bestellen“"
            />
          </Feld>

          <Feld
            titel="Produktbild"
            art="optional"
            wofuer="Freigestelltes Produktbild auf der Kaufkarte. Am einfachsten unten aus den Produkt-Medien wählen; sonst eine https-Adresse eintragen. Leer = Karte ohne Bild."
            beispiele={{ feld: 'raum.offer_image_url', webinarId, onUebernehmen: (v) => setF('offer_image_url', v) }}
          >
            {medien && medien.bilder.length > 0 && (
              <Box mb={1}>
                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                  Aus den Produkt-Medien{medien.produkt ? ` von „${medien.produkt}“` : ''} wählen (Klick trägt das Bild ein):
                </Typography>
                <Stack direction="row" gap={1} flexWrap="wrap">
                  {medien.bilder.map((b) => {
                    const aktiv = form.offer_image_url.trim() === b.url;
                    return (
                      <Tooltip key={b.url} title={b.label} arrow>
                        <Box
                          component="button"
                          type="button"
                          onClick={() => setF('offer_image_url', b.url)}
                          sx={{
                            p: 0.5,
                            border: aktiv ? '3px solid #4cceac' : '1px solid #c2c2c2',
                            borderRadius: 1,
                            bgcolor: aktiv ? '#eefaf6' : '#ffffff',
                            cursor: 'pointer',
                            lineHeight: 0,
                          }}
                        >
                          <img src={b.url} alt={b.label} width={84} height={84} style={{ objectFit: 'contain', display: 'block' }} />
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Stack>
              </Box>
            )}
            {medien?.hinweis && (
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                {medien.hinweis}
              </Typography>
            )}
            <TextField
              fullWidth
              size="small"
              label="Bild-Adresse (https)"
              value={form.offer_image_url}
              onChange={(e) => setF('offer_image_url', e.target.value)}
              placeholder="z. B. https://…/produktbild.png"
            />
          </Feld>

          <Feld
            titel="Bildposition"
            art="optional"
            wofuer="„Rechts“ gilt am Handy, nach dem Video und im Kauf-Fenster; auf dem Desktop neben dem Video steht das Bild dann groß über dem Titel."
          >
            <RadioGroup
              row
              value={form.offer_image_pos}
              onChange={(e) => setF('offer_image_pos', e.target.value === 'right' ? 'right' : 'top')}
            >
              <FormControlLabel value="top" control={<Radio size="small" />} label="Über dem Titel" />
              <FormControlLabel value="right" control={<Radio size="small" />} label="Rechts neben Titel und Unterzeile" />
            </RadioGroup>
          </Feld>

          <Feld
            titel="Nutzen-Punkte"
            art="empfohlen"
            wofuer="Die Gründe zum Kaufen auf der Kaufkarte — eine Zeile je Punkt, höchstens 6, kurz und konkret, am besten wörtlich von der Bestellseite. Leer = die Karte zeigt nur Titel und Preis."
            beispiele={{ feld: 'raum.offer_bullets', webinarId, onUebernehmen: (v) => setF('offer_bullets', v) }}
            befunde={pruefPunkte.befunde}
            pruefungLaeuft={pruefPunkte.laeuft}
          >
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={3}
              value={form.offer_bullets}
              onChange={(e) => setF('offer_bullets', e.target.value)}
              onBlur={() => void pruefPunkte.pruefen(form.offer_bullets, { mitKi: true })}
            />
          </Feld>

          <Feld
            titel="Kennzahl"
            art="optional"
            wofuer="Nur belegbare Zahlen. Der Raum zeigt dazu immer: „Frühere Wertentwicklungen sind kein verlässlicher Indikator für künftige Ergebnisse.“ Leer = keine Kennzahl."
            beispiele={{ feld: 'raum.offer_highlight', webinarId, onUebernehmen: (v) => setF('offer_highlight', v) }}
            befunde={pruefKennzahl.befunde}
            pruefungLaeuft={pruefKennzahl.laeuft}
          >
            <TextField
              fullWidth
              size="small"
              value={form.offer_highlight}
              onChange={(e) => setF('offer_highlight', e.target.value)}
              onBlur={() => void pruefKennzahl.pruefen(form.offer_highlight, { mitKi: true })}
              placeholder="z. B. Ø + … % pro Trade (nur belegbare Zahl)"
            />
          </Feld>

          <Box sx={{ p: 1.5, border: '1px solid #e5e7eb', borderRadius: 1, bgcolor: '#fcfcfd' }}>
            <Typography variant="subtitle2" gutterBottom>
              Wohin führt der Kaufknopf?
            </Typography>
            {imEmbed ? (
              <Typography variant="body2">
                Der Kauf läuft heute direkt im Raum über das eingebettete Bestellformular (vom Admin eingestellt).
              </Typography>
            ) : kaufZiel ? (
              <>
                <Typography variant="body2">
                  Der Kaufknopf führt heute auf{' '}
                  <Box component="span" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                    {kaufZiel}
                  </Box>
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Quelle: {kaufQuelle}. Werbe-Kennung und persönlicher Code werden im Raum automatisch angehängt — hier
                  ist nichts einzutragen.
                </Typography>
              </>
            ) : (
              <Alert severity="warning">
                Der Kaufknopf hat noch kein Ziel. Erst die Bestellstrecke dieses Webinars live schalten — danach führt der
                Knopf automatisch dorthin.
              </Alert>
            )}
          </Box>
        </Box>
        <SpeichernLeiste
          dirty={dirtyAngebot}
          laeuft={savingSection === 'Kaufangebot'}
          gesperrt={savingSection !== null || saving}
          onSpeichern={() => void saveSection('Kaufangebot', ANGEBOT_KEYS)}
        />
      </Abschnitt>

      {/* 4. Warteraum-Bild */}
      <Abschnitt
        id="raum-warteraum"
        titel="Warteraum-Bild"
        art="optional"
        eingeklappt
        erklaerung={`Hintergrund des Warteraums vor dem Start. Ohne Auswahl: Standbild bei Sekunde ${POSTER_DEFAULT_TIME_SEC} des Videos.${dirtyPoster ? ' — Ungespeicherte Änderungen.' : ''}`}
      >
        <Stack spacing={2} maxWidth={760}>
          {cfg?.mux_playback_id && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                Standbild wählen (Klick übernimmt die Stelle):
              </Typography>
              <Stack direction="row" gap={1} flexWrap="wrap">
                {posterCandidates(durationSec).map((s) => {
                  const aktiv = !form.poster_image_url.trim() && posterSecOf(form.poster_time_sec) === s;
                  return (
                    <Box
                      key={s}
                      component="button"
                      type="button"
                      onClick={() => setF('poster_time_sec', String(s))}
                      title={`Standbild bei ${formatPos(s)}`}
                      sx={{
                        p: 0,
                        border: aktiv ? '3px solid #4cceac' : '1px solid #c2c2c2',
                        borderRadius: 1,
                        overflow: 'hidden',
                        cursor: 'pointer',
                        background: '#000',
                        lineHeight: 0,
                        position: 'relative',
                      }}
                    >
                      <img src={muxThumb(cfg.mux_playback_id as string, s, 160)} alt="" width={120} height={68} style={{ objectFit: 'cover', display: 'block' }} />
                      <Box component="span" sx={{ position: 'absolute', right: 3, bottom: 3, background: 'rgba(0,0,0,.7)', color: '#fff', fontSize: 11, lineHeight: 1.4, px: 0.5, borderRadius: 0.5 }}>
                        {formatPos(s)}
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <Stack spacing={2} flex={1}>
              <TextField
                label="Standbild bei Sekunde"
                size="small"
                type="number"
                inputProps={{ min: 0 }}
                value={form.poster_time_sec}
                onChange={(e) => setF('poster_time_sec', e.target.value)}
                helperText={`Leer = Sekunde ${POSTER_DEFAULT_TIME_SEC}. Countdown und Titel liegen links — am besten ein Bild mit dem Referenten rechts.`}
              />
              {videoReady && previewUrl && (
                <Box>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const t = videoRef.current?.currentTime;
                      if (typeof t === 'number' && Number.isFinite(t)) setF('poster_time_sec', String(Math.floor(t)));
                    }}
                  >
                    Stelle aus der Vorschau übernehmen
                  </Button>
                  <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                    In der Video-Vorschau (Abschnitt „Video“) zur gewünschten Stelle spulen, dann hier klicken.
                  </Typography>
                </Box>
              )}
              <TextField
                label="Oder eigenes Bild (https-Adresse)"
                size="small"
                value={form.poster_image_url}
                onChange={(e) => setF('poster_image_url', e.target.value)}
                helperText="Gewinnt vor dem Standbild — z. B. Porträt des Referenten, Querformat 16:9. Der Raum dunkelt es ab und legt Countdown und Titel links darüber."
              />
            </Stack>
            {posterPreview && (
              <Box sx={{ width: 300, maxWidth: '100%', flex: 'none' }}>
                <Box sx={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', borderRadius: 1, overflow: 'hidden', background: '#000' }}>
                  <Box
                    component="img"
                    src={posterPreview}
                    alt="Vorschau Warteraum-Bild"
                    sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: 'saturate(.9) brightness(.8)' }}
                  />
                  {/* Derselbe Verlauf wie .wait in room.html — links liegen Countdown und Titel. */}
                  <Box sx={{ position: 'absolute', inset: 0, background: 'linear-gradient(105deg,rgba(12,15,19,.94) 38%,rgba(12,15,19,.35))' }} />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Vorschau wie im Warteraum — gilt nach dem Speichern.
                </Typography>
              </Box>
            )}
          </Stack>
        </Stack>
        <SpeichernLeiste
          dirty={dirtyPoster}
          laeuft={savingSection === 'Warteraum-Bild'}
          gesperrt={savingSection !== null || saving}
          onSpeichern={() => void saveSection('Warteraum-Bild', POSTER_KEYS)}
        />
      </Abschnitt>

      {/* 4b. Werbebild (Portale, Kursgespraech-Seite, DOI) — nie Warteraum */}
      {webinarId && !view.webinar.is_evergreen && !view.webinar.is_recurring && (
        <Abschnitt
          id="raum-werbebild"
          titel="Werbebild (Portale & DOI-Seiten)"
          art="optional"
          eingeklappt
          erklaerung="KI-Bild mit Text aus der Werbe-Copy für die Webinar-Karte auf den Portalen und den DOI-Seiten. Nicht im Warteraum."
        >
          <WerbebildPanel webinarId={webinarId} />
        </Abschnitt>
      )}

      {/* 5. Testen */}
      <Abschnitt
        id="raum-testen"
        titel="Testen"
        eingeklappt
        erklaerung="Öffnet den Raum in einem neuen Tab an einer bestimmten Stelle der Sendung — auch bei gesperrtem Raum. Zählt nicht mit und meldet niemanden an. Getestet wird der gespeicherte Stand."
      >
        <Stack spacing={2} maxWidth={760}>
          {globalBlock && <Alert severity="info">{globalBlock}</Alert>}
          <Typography variant="subtitle2">Test-Link öffnen bei:</Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
            {probePresets.map((p) => (
              <Tooltip key={p.key} title={p.blocked || p.hint} arrow>
                <span>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={probeBusy === p.key ? <CircularProgress size={14} /> : <OpenInNewIcon />}
                    disabled={p.joinAtSec === null || probeBusy !== null}
                    onClick={() => openProbe(p)}
                  >
                    {p.label}
                  </Button>
                </span>
              </Tooltip>
            ))}
          </Stack>
          {!globalBlock && offerBlocked && (
            <Typography variant="caption" color="text.secondary">
              „1 Min vor Angebot“ und „Angebot erscheint“ gehen gerade nicht: {offerBlocked}
            </Typography>
          )}
          <Stack direction="row" flexWrap="wrap" gap={2} alignItems="center">
            <TextField
              size="small"
              label="Minute X"
              value={probeMinuteX}
              onChange={(e) => setProbeMinuteX(e.target.value)}
              error={Boolean(minuteXBlocked) && !globalBlock}
              helperText={!globalBlock && minuteXBlocked ? minuteXBlocked : 'negativ = vor dem Start'}
              inputProps={{ inputMode: 'decimal' }}
              sx={{ width: 170 }}
            />
            <RadioGroup
              row
              value={probeLayout}
              onChange={(e) => setProbeLayout(e.target.value === 'box' ? 'box' : 'studio')}
            >
              <FormControlLabel value="studio" control={<Radio size="small" />} label="Ansicht „Studio“" />
              <FormControlLabel value="box" control={<Radio size="small" />} label="Ansicht „Box“" />
            </RadioGroup>
            <FormControlLabel
              control={<Switch checked={probeWithCode} onChange={(e) => setProbeWithCode(e.target.checked)} />}
              label="Mit meinem Zugang"
            />
            <NurAdmin>
              <FormControlLabel
                control={<Switch checked={probeDebug} onChange={(e) => setProbeDebug(e.target.checked)} />}
                label="Debug-Anzeige (Admin)"
              />
            </NurAdmin>
          </Stack>
          <Typography variant="caption" color="text.secondary">
            Ansicht „Studio“: so sehen alle Zuschauer den Raum — die Kaufkarte steht rechts neben dem Video. Ansicht „Box“:
            eine Variante mit der Kaufkarte als Kasten unter dem Video (sehen Zuschauer nur über einen eigenen Link).
            „Mit meinem Zugang“ hängt deinen persönlichen Code an, wenn du für dieses Webinar angemeldet bist — sonst
            fragt der Raum nach der E-Mail einer bestehenden Anmeldung. Zum Weitergeben an Kollegen ausschalten.
          </Typography>
          {probeResult && (
            <Stack spacing={0.5}>
              {probeResult.popupBlocked && (
                <Alert severity="warning">Der Browser hat den neuen Tab blockiert — Link unten öffnen.</Alert>
              )}
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <Typography variant="body2">
                  Letzter Test-Link: <strong>{probeResult.label}</strong> · gültig bis {formatUntil(probeResult.expiresMs)} Uhr
                </Typography>
                <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => copyToClipboard(probeResult.url)}>
                  Kopieren
                </Button>
                <Button size="small" startIcon={<OpenInNewIcon />} href={probeResult.url} target="_blank" rel="noopener">
                  Öffnen
                </Button>
              </Stack>
              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {probeResult.url}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Der Zeitpunkt läuft ab dem Klick — wer den Link später öffnet, steigt entsprechend später ein.
                {probeResult.hasCode &&
                  ' Enthält deinen persönlichen Zugang (Anwesenheit und Bestellungen laufen auf dich) — zum Weitergeben „Mit meinem Zugang“ ausschalten.'}
                {probeResult.codeWanted &&
                  !probeResult.hasCode &&
                  ' Deine Adresse ist für dieses Webinar nicht angemeldet — der Raum fragt nach der E-Mail einer bestehenden Anmeldung.'}
              </Typography>
            </Stack>
          )}
          <Divider />
          <Typography variant="subtitle2">Persönlichen Raum-Link einer Anmeldung zeigen</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <TextField
              size="small"
              label="E-Mail einer Anmeldung"
              value={lookupEmail}
              onChange={(e) => setLookupEmail(e.target.value)}
              sx={{ minWidth: { sm: 320 } }}
            />
            <Button variant="outlined" onClick={lookupPersonalLink} disabled={!lookupEmail.trim()}>
              Persönlichen Link zeigen
            </Button>
          </Stack>
          {personalLink && (
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {personalLink}
              </Typography>
              <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => copyToClipboard(personalLink)}>
                Kopieren
              </Button>
            </Stack>
          )}
          <Eingeklappt
            titel="Aufzeichnung im Raum testen"
            erklaerung="Testlinks für beide Wege nach der Sendung (Aufzeichnung im Raum und Aufzeichnungsseite) — nur bei Bedarf."
          >
            <Stack spacing={1.5}>
              <Typography variant="caption" color="text.secondary">
                Die Links zeigen beide Wege, ohne den Test einzuschalten, und zählen in der Test-Auswertung nicht mit.
                Sie laufen auf die Adresse im Feld „E-Mail einer Anmeldung“ (leer = deine eigene) — nur Team-Adressen, die
                für dieses Webinar angemeldet sind.
              </Typography>
              <Box>
                <Button variant="outlined" onClick={loadOdTestLinks} disabled={odTestBusy}>
                  Testlinks erzeugen
                </Button>
              </Box>
              {odTestError && <Alert severity="warning">{odTestError}</Alert>}
              {odTest && (
                <Stack spacing={1.5}>
                  <Alert severity="info">
                    Jeder Link beginnt bei jedem Öffnen von vorn. Das Video startet im Raum ohne Ton — dort auf „Ton
                    einschalten“ klicken. Die Links gelten fürs ganze Team: jedes Öffnen ist ein eigener Testlauf, auch
                    wenn mehrere Personen denselben Link gleichzeitig nutzen. „Als Link kopieren“ legt Kurzlinks ab, die
                    Teams und Outlook nicht abschneiden. Bestellungen sind auf beiden Wegen echt: als PLZ 53119 eintragen.
                    Anmeldung: {odTest.email} · gültig bis {formatUntil(odTest.expiresMs)}.
                  </Alert>
                  {odTest.sets.map((set) => (
                    <Stack key={set.nr} spacing={0.5}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }}>
                          {odTest.sets.length > 1 ? `Person ${set.nr}` : 'Linksatz'}
                        </Typography>
                        <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => copyLinksAsText(set.links.map(teilbarerLink))}>
                          Alle als Links kopieren
                        </Button>
                      </Stack>
                      {set.links.map((l) => (
                        <Stack key={l.key} direction="row" spacing={1} alignItems="center">
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {l.label}
                          </Typography>
                          <Button size="small" startIcon={<ContentCopyIcon />} onClick={() => copyLinksAsText([teilbarerLink(l)])}>
                            Als Link kopieren
                          </Button>
                          <Button size="small" startIcon={<OpenInNewIcon />} href={l.url} target="_blank" rel="noopener">
                            Öffnen
                          </Button>
                        </Stack>
                      ))}
                    </Stack>
                  ))}
                </Stack>
              )}
            </Stack>
          </Eingeklappt>
        </Stack>
      </Abschnitt>

      {/* 6. Läuft automatisch */}
      <Abschnitt
        id="raum-automatisch"
        titel="Läuft automatisch"
        art="automatisch"
        erklaerung="Das System erledigt diese Punkte selbst. Hier ist nichts zu tun — nur ansehen oder im Ausnahmefall abschalten."
      >
        <Stack spacing={2.5}>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Werbung auf den Portalen am Webinartag
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              Am Webinartag zeigen die Finanzportale eine Karte zum Webinar, kurz vor dem Start mit Countdown, ab Start
              „LIVE – jetzt einsteigen“.
            </Typography>
            <FormControlLabel
              control={<Switch checked={promoOn} disabled={saving || unsupported} onChange={(e) => togglePromo(e.target.checked)} />}
              label="Portal-Werbung am Webinartag erlauben"
            />
            <Typography variant="body2" sx={{ mb: 0.5 }} color={!promoOn ? 'text.secondary' : bewerbbar === false ? '#8a4b00' : 'text.secondary'}>
              {!promoOn
                ? 'Abgeschaltet — die Portale zeigen für dieses Webinar keine Werbung.'
                : bewerbbar === true
                  ? 'Erlaubt, und das Webinar ist bewerbbar — die Werbung erscheint am Webinartag.'
                  : bewerbbar === false
                    ? 'Erlaubt — erscheint aber erst, wenn das Webinar bewerbbar ist. Aktuell ist es das noch nicht, siehe Fahrplan.'
                    : 'Erlaubt — erscheint erst, wenn das Webinar bewerbbar ist (Stand im Fahrplan).'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {view.late_entry_until_ms
                ? `Einstieg nach dem Start möglich bis ${new Date(view.late_entry_until_ms).toLocaleTimeString('de-DE', {
                    hour: '2-digit',
                    minute: '2-digit',
                    timeZone: 'Europe/Berlin',
                  })} Uhr — so lange läuft auch die Werbung.`
                : 'Kein Einstieg nach dem Start (Raum gesperrt oder Video nicht fertig) — die Werbung endet beim Start.'}
            </Typography>
          </Box>
          <Divider />
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Webinar-Karte auf den DOI-Danke-Seiten
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Wer sich auf den Portalen für einen Newsletter anmeldet, sieht danach eine Karte zu diesem Webinar. Sie
              entsteht automatisch aus den Heat-Up-Mails.
            </Typography>
            <DoiKarteStatus k={doiKarte} admin={istAdmin} />
          </Box>
          <Divider />
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Untertitel
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {cfg && cfg.video_status !== 'none'
                ? UNTERTITEL_TEXT[cfg.captions_status || 'none']
                : 'Entstehen automatisch, sobald ein Video hochgeladen ist.'}
            </Typography>
          </Box>
        </Stack>
      </Abschnitt>

      {/* 7. Notfall */}
      <Abschnitt
        id="raum-notfall"
        titel="Notfall"
        erklaerung="Nur wenn etwas schiefläuft (falsches Video, rechtliches Problem): Raum sperren stoppt Werbung und Zugang sofort."
      >
        {roomOn ? (
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              setSperrFehler(null);
              setSperrDialog(true);
            }}
            disabled={saving}
          >
            Raum sperren …
          </Button>
        ) : (
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Typography variant="body2" color="error">
              Der Raum ist gesperrt.
            </Typography>
            <Button variant="outlined" onClick={() => setEntsperrDialog(true)} disabled={saving || unsupported}>
              Raum entsperren …
            </Button>
          </Stack>
        )}
      </Abschnitt>

      <GefahrBestaetigung
        open={sperrDialog}
        titel="Raum sperren (Notfall)?"
        wirkung={sperrWirkung}
        knopfText="Raum sperren"
        bestaetigungsWort={w.webinar_id}
        laeuft={saving}
        fehler={sperrFehler}
        onAbbrechen={() => setSperrDialog(false)}
        onBestaetigen={() => {
          setSperrFehler(null);
          void toggleRoom(false).then((ok) => {
            if (ok) setSperrDialog(false);
          });
        }}
      />
      <Dialog open={entsperrDialog} onClose={() => setEntsperrDialog(false)}>
        <DialogTitle>Raum entsperren?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Danach ist der Raum wieder offen: Angemeldete kommen über ihren Link hinein, und das Webinar wird wieder
            beworben, sofern es bewerbbar ist.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEntsperrDialog(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            disabled={saving}
            onClick={() => {
              setEntsperrDialog(false);
              void toggleRoom(true);
            }}
          >
            Entsperren
          </Button>
        </DialogActions>
      </Dialog>

      {/* 8. Technik (nur Admin) */}
      <NurAdmin bereich titel="Technik">
        <Stack spacing={2} maxWidth={760}>
          <Box>
            <Typography variant="subtitle2">Kaufweg-A/B (nur bei „Weiterleitung“)</Typography>
            <Typography variant="body2" color="text.secondary" mb={1}>
              Arm P = Bestell-URL unten bzw. des Webinars, Arm H = gehostete Bestellstrecke. Jeder Zuschauer bekommt seinen
              Arm beim ersten Zugang und behält ihn — ein späterer Wechsel des Anteils gilt nur für neue Zuschauer. Not-Aus:
              Bestell-URL Arm H leeren und speichern ⇒ alle bekommen sofort P, der Anteil geht auf 0.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                size="small"
                label="Bestell-URL Arm H (gehostet)"
                value={form.offer_order_url_h}
                onChange={(e) => setF('offer_order_url_h', e.target.value)}
                placeholder={`z. B. https://lp.finanztrends.de/w/${w.webinar_id}/bestellen/`}
                helperText="Nur eine publizierte Bestellseite. Leer = kein A/B, alle bekommen P."
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                label="Anteil H in %"
                type="number"
                value={form.purchase_ab_percent}
                onChange={(e) => setF('purchase_ab_percent', e.target.value)}
                inputProps={{ min: 0, max: 100, step: 5 }}
                helperText="0 = alle P. Über 0 nur mit Arm-H-URL und Weiterleitung."
                sx={{ width: { xs: '100%', sm: 200 } }}
              />
            </Stack>
          </Box>
          <Box>
            <Typography variant="subtitle2">Kaufweg</Typography>
            <RadioGroup
              row
              value={form.offer_mode}
              onChange={(e) => setF('offer_mode', e.target.value === 'embed' ? 'embed' : 'redirect')}
            >
              <FormControlLabel value="redirect" control={<Radio size="small" />} label="Weiterleitung (Bestellseite im neuen Tab)" />
              <FormControlLabel value="embed" control={<Radio size="small" />} label="Einbettung (PL-X-Bestellformular im Raum)" />
            </RadioGroup>
            <TextField
              size="small"
              label="PL-X-Kampagnen-ID"
              value={form.plx_campaign_id}
              onChange={(e) => setF('plx_campaign_id', e.target.value)}
              placeholder="z. B. 93791"
              sx={{ mt: 1 }}
              helperText={
                form.offer_mode === 'embed'
                  ? 'Pflicht für die Einbettung (data-campaign des PL-X-Formulars).'
                  : 'Optional: gesetzt, sehen interne Tester (@yes-investmedia.de, @finanztrends.de) im Raum oben einen Umschalter „Kaufweg“. Zuschauer sehen weiter nur die Weiterleitung.'
              }
            />
            {form.offer_mode === 'embed' && (
              <Alert severity="info" sx={{ mt: 1 }}>
                Der Bestellknopf des PL-X-Formulars verlässt kurz die Seite; zurück nur über die „Finale Weiterleitung“ der
                PL-X-Kampagne. Als Bestell-Herkunft speichert PL-X die Seite mit dem Formular (Raum-URL mit Banner und
                persönlichem Code).
              </Alert>
            )}
          </Box>
          <TextField
            size="small"
            label="Bestell-URL des Raums (leer = Bestell-URL des Webinars)"
            value={form.offer_order_url}
            onChange={(e) => setF('offer_order_url', e.target.value)}
            placeholder={w.order_url ? `leer = ${w.order_url}` : 'leer = Bestell-URL des Webinars'}
            helperText="Kaufweg P. Nur setzen, wenn der Raum bewusst woanders hinführen soll als die live geschaltete Bestellstrecke. Banner und persönlicher Code werden automatisch angehängt."
          />
          {view.order_link_preview && (
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
              Bestell-Link Arm P (ohne persönlichen Code): {view.order_link_preview}
            </Typography>
          )}
          {view.order_link_preview_h && (
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
              Bestell-Link Arm H (ohne persönlichen Code, Anteil {Number(cfg?.purchase_ab_percent) || 0} %):{' '}
              {view.order_link_preview_h}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
            Nach dem Video für Nicht-Zuschauer (unter 30 Sekunden geschaut): Weiterleitung auf{' '}
            {view.replay_redirect_preview || 'keine Aufzeichnungsseite hinterlegt — der Raum zeigt dann einen Hinweis'}
          </Typography>
          <TextField
            type="date"
            size="small"
            label="Aufzeichnungsdatum"
            InputLabelProps={{ shrink: true }}
            value={form.recorded_on}
            onChange={(e) => setF('recorded_on', e.target.value)}
            helperText="Leer = keine Kennzeichnung (Standard). Gesetzt = „Aufzeichnung vom …“ im Bild. „LIVE“ zeigt der Raum nie."
            sx={{ maxWidth: 320 }}
          />
          <Divider />
          <Typography variant="subtitle2">Webinar-Werbung auf DOI-Seiten — nur alte Karte</Typography>
          <Typography variant="body2" color="text.secondary">
            Gilt nur für die alte Karte (DOI-Seiten ohne A/B-Test, ohne gültiges Faktenblatt oder mit „Bisherige Karte
            verwenden“); die neue Karte liest diese Felder nicht. Leer = automatisch: öffentlicher Titel, Terminzeile
            (immer vom System), Raum-Standbild. Zusatztext steht hinter dem Termin.
          </Typography>
          <TextField
            size="small"
            label="Überschrift (nur alte Karte)"
            value={form.doi_headline}
            onChange={(e) => setF('doi_headline', e.target.value)}
            placeholder={w.title ? `leer = „${w.title}“` : 'leer = öffentlicher Titel'}
            helperText={
              form.doi_headline.length > DOI_HEADLINE_MAX
                ? `Höchstens ${DOI_HEADLINE_MAX} Zeichen (jetzt ${form.doi_headline.length}) — der Rest wird abgeschnitten.`
                : `Leer = öffentlicher Titel. Höchstens ${DOI_HEADLINE_MAX} Zeichen.`
            }
            error={form.doi_headline.length > DOI_HEADLINE_MAX}
          />
          <TextField
            size="small"
            label="Zusatztext hinter dem Termin (nur alte Karte)"
            value={form.doi_subline}
            onChange={(e) => setF('doi_subline', e.target.value)}
            placeholder={w.host_name ? `leer = „Live mit ${w.host_name}“` : 'leer = „Live mit <Referent>“'}
            helperText={
              form.doi_subline.length > DOI_SUBLINE_MAX
                ? `Höchstens ${DOI_SUBLINE_MAX} Zeichen (jetzt ${form.doi_subline.length}) — der Rest wird abgeschnitten.`
                : `Leer = „Live mit <Referent>“. Höchstens ${DOI_SUBLINE_MAX} Zeichen, eine Zeile.`
            }
            error={form.doi_subline.length > DOI_SUBLINE_MAX}
          />
          <TextField
            size="small"
            label="Bild (https-Adresse, nur alte Karte)"
            value={form.doi_image_url}
            onChange={(e) => setF('doi_image_url', e.target.value)}
            placeholder="z. B. https://image.mux.com/…/thumbnail.jpg"
            helperText="Leer = Raum-Standbild. Nur https auf lp./db.finanztrends.de, *.pl-x.de, i.vimeocdn.com oder image.mux.com — andere Bilder zeigt die DOI-Seite nicht."
          />
          {view.rollout_counts &&
            view.rollout_counts.room + view.rollout_counts.ss + view.rollout_counts.testers > 0 && (
              <Typography variant="caption" color="text.secondary">
                Historie des früheren Raum-Anteils (bis zur SS-Abschaltung 02.10.): Raum-Link {view.rollout_counts.room} ·
                StealthSeminar {view.rollout_counts.ss} · Team-Adressen {view.rollout_counts.testers}
              </Typography>
            )}
          <Typography variant="caption" color="text.secondary">
            Vorschau der Portal-Werbung auf jeder Artikelseite: <code>?webinar_promo_preview=1&amp;webinar_promo_at=-20</code>{' '}
            (Minuten relativ zum Start; zeigt das nächste bewerbbare Webinar, zählt nicht mit).
          </Typography>
        </Stack>
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Button
              variant="contained"
              onClick={() => void saveSection('Technik', TECHNIK_KEYS)}
              disabled={!dirtyTechnik || savingSection !== null || saving}
            >
              {savingSection === 'Technik' ? 'Speichert …' : 'Technik speichern'}
            </Button>
            <Typography variant="body2" color={dirtyTechnik ? '#8a4b00' : 'text.secondary'}>
              {dirtyTechnik ? 'Ungespeicherte Änderungen in „Technik“.' : 'Alles gespeichert.'}
            </Typography>
          </Stack>
        </Box>
        <Box sx={{ mt: 3 }}>
          <DoiKarteEditor k={doiKarte} />
        </Box>
      </NurAdmin>
    </Box>
  );
};

export default RoomTab;
